/**
 * Authentication Provider Component
 *
 * This component manages the authentication flow for the application.
 * It handles SSO token processing and route protection.
 *
 * Authentication Flow:
 * 1. User clicks login -> Redirected to SSO (MyID)
 * 2. SSO authenticates -> Redirects back with ?token=xxx in URL
 * 3. SecureLogin extracts token from URL query params
 * 4. Token is dispatched to Redux -> fetchUserPermissions validates it
 * 5. On success -> User redirected to their intended destination
 * 6. On failure -> Error displayed on login page
 *
 * Route Structure:
 * - /login    -> Login page (SecureLogin handles token extraction)
 * - /         -> Root redirects to login for token processing
 * - /*        -> All other routes require authentication (SecuredApp)
 *
 * @see src/datastage/store/auth - Redux auth actions and reducers
 * @see src/datastage/config/constants.js - SSO URLs configuration
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserPermissions } from '../datastage/store/auth';
import { SecuredApp } from './SecuredApp/SecuredApp';
import DatastageRouter from '../datastage/router';
import { setAuthError } from '../datastage/store/auth/actions';
import parse from 'html-react-parser';
import {
    normalizePostLoginRedirect,
    shouldStayOnLoginForDealVersionDownload,
} from '../datastage/utils/dealVersionDownloadRedirect.js';

/**
 * SecureLogin Component
 *
 * Handles the login callback from SSO.
 * Extracts token from URL and validates user permissions.
 *
 * @param {Object} props
 * @param {React.Component} props.LoginComp - The login page component to render
 */
export function SecureLogin({ LoginComp }) {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { auth } = useSelector((s) => s);

    // Process token and postLoginRedirect from URL when query params change
    useEffect(() => {
        async function setTokenAsync() {
            await setToken();
        }

        const values = queryString.parse(location.search);
        const postLoginRedirectRaw = values.postLoginRedirect;
        if (postLoginRedirectRaw) {
            const normalized = normalizePostLoginRedirect(postLoginRedirectRaw);
            if (normalized) {
                localStorage.setItem('postLoginRedirect', normalized);
            }
        }

        setTokenAsync();
        // eslint-disable-next-line
    }, [location.search, auth?.token]);

    // Redirect user after successful authentication
    useEffect(() => {
        checkCredentials();
        // eslint-disable-next-line
    }, [auth?.token]);

    /**
     * Extracts and processes the authentication token from URL
     * Token format: ?token=xxx or ?error=xxx (if SSO failed)
     */
    async function setToken() {
        const values = queryString.parse(location.search);
        let token = '';
        if (values.token) {
            token = values.token;
            dispatch(fetchUserPermissions(token));
        } else {
            await dispatch(setAuthError(parse(values.error || '')));
        }
    }

    /**
     * Redirects authenticated users to their destination
     * Uses postLoginRedirect from localStorage if available
     */
    function checkCredentials() {
        const withCredentials = auth?.token;
        const values = queryString.parse(location.search);
        const tokenInUrl = Boolean(values.token);

        if (withCredentials) {
            const redirectPath = localStorage.getItem('postLoginRedirect');
            if (
                shouldStayOnLoginForDealVersionDownload({
                    redirectPath,
                    tokenInUrl,
                    hasAuthToken: true,
                })
            ) {
                return;
            }
            // If no redirectPath, just go to /products
            if (!redirectPath) {
                localStorage.removeItem('postLoginRedirect');
                navigate('/products', { replace: true });
                return;
            }
            // Prevent redirect loop: if already at the redirectPath, do nothing
            if (
                (window.location.href === redirectPath) ||
                (window.location.pathname + window.location.search === redirectPath)
            ) {
                localStorage.removeItem('postLoginRedirect');
                return;
            }
            // Remove before redirect
            localStorage.removeItem('postLoginRedirect');
            // If redirectPath is an absolute URL, use window.location.replace
            if (/^https?:\/\//i.test(redirectPath)) {
                // Full-page download / API URL — do not store as activePanel; AuthVerify navigates
                // based on activePanel and would re-open this URL after the next login.
                window.location.replace(redirectPath);
            } else {
                navigate(redirectPath, { replace: true });
                localStorage.setItem('activePanel', redirectPath);
            }
        } else if (location.pathname !== '/login' && location.pathname !== '/') {
            navigate('/login', { replace: true });
        }
    }

    return <LoginComp loginFailed />;
}

/**
 * AuthProvider Component
 *
 * Top-level routing component that separates public and protected routes.
 *
 * @param {Object} props
 * @param {React.Component} props.AppComp - The authenticated app component
 * @param {React.Component} props.LoginComp - The login page component
 */
export function AuthProvider({ AppComp, LoginComp, ...props }) {
    return (
        <Routes>
            {/* Public routes - login page and SSO callback */}
            <Route path="/login" element={<SecureLogin LoginComp={LoginComp} {...props} />} />
            <Route path="/" element={<SecureLogin LoginComp={LoginComp} {...props} />} />

            {/* Protected routes - requires authentication */}
            <Route path="/*" element={<SecuredApp App={DatastageRouter} />} />

            {/* Fallback - redirect unknown routes to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
}
